/**
 * ADR-0212 §5 — one phrasing per fact. The install tabs, the model
 * presentation and the coverage card import from here so the same
 * sentence never drifts across three files again.
 */

/** The four layers, in the merchant's words (chips + coverage card rows). */
export const LAYER_LABEL = {
  browser: 'People and browser agents',
  server_side: "Agents that don't run JavaScript",
  discovery: 'Agent card and files',
  store: 'Store catalog and orders',
} as const;

export type LayerKey = keyof typeof LAYER_LABEL;

/** Under the Verify button — the one hint. */
export const VERIFY_HINT = 'We check your homepage for the snippet.';

/** Inside "What the Worker does". */
export const WORKER_DOES_BODY =
  'A Sill-managed Worker on your Cloudflare zone sees every request before your server does: it records every agent, adds the browser script to your pages, and serves your agent card and files. It installs in under 30 seconds with a scoped API token.';

export function workerDeployedBody(zone: string | null): string {
  return `Your Worker is live${zone !== null ? ` on ${zone}` : ''}. It records every agent, adds the browser script, and serves your agent card and files.`;
}

/** WordPress / WooCommerce plugin — one sentence above the fold. */
export const PLUGIN_SENTENCE =
  'One upload records every agent and serves your agent card.';
export const PLUGIN_SENTENCE_WOO =
  'WooCommerce runs on WordPress, so the same plugin records every agent, adds the browser script, and serves your files.';
export const PLUGIN_HOW = 'How to install it';
export const PLUGIN_HOW_BODY =
  'Download the ZIP, then in WordPress go to Plugins → Add New → Upload Plugin and choose sill-agent-ready.zip. Activate it. It proves you own the domain on its own — nothing to paste. From version 1.23 onwards, later updates come through WordPress → Plugins so you do not download the ZIP again.';

/** Orange-clouded WordPress / WooCommerce — the Worker takes Step 1. */
export const WORKER_INSTEAD_SENTENCE =
  'Cloudflare caches pages before WordPress sees them; the Worker sees every request.';

/** Shopify. */
export const SHOPIFY_SENTENCE =
  'One connect adds the browser script, proves you own the store, and lets agents read your catalog and orders.';
export const SHOPIFY_WHAT = 'What connecting installs';
export const SHOPIFY_WHAT_BODY =
  'The Sill app adds the browser script to your storefront, records the store as verified, serves your agent card at /apps/sill/agent-card.json on your store domain, and syncs your catalog and orders so agents can read them. Checkout activates later, once payments are connected.';

/** Script tag. */
export const SCRIPT_SENTENCE =
  'Paste this before </body> on every page. It sees visitors and agents that run JavaScript.';
export const SCRIPT_WHAT = 'What the script does';
export const SCRIPT_WHAT_BODY =
  'The script sends Sill one beacon per page view — no fingerprinting, and no cookies or stored IDs unless you turn on returning visitors — and advertises your agent card to agents that read the page. It cannot see crawlers that never run JavaScript; a plugin or the Cloudflare Worker covers those.';
export const STATIC_CARD_SENTENCE =
  'Redirect /.well-known/agent-card.json to Sill so crawlers find you.';
export const STATIC_CARD_OTHER = 'Other ways to serve it';

/**
 * 2026-10-28 — Shopify serves its own /llms.txt, /agents.md and
 * /.well-known/ucp on every store. Two optional steps after connecting
 * point agents at Sill's documents under /apps/sill (the app proxy),
 * plus the Cloudflare route for stores whose domain is on Cloudflare.
 */
export const SHOPIFY_AGENTS_MD_TITLE = 'Add Sill to your agents.md';
export const SHOPIFY_AGENTS_MD_SENTENCE =
  'Shopify publishes a file at /agents.md that AI agents read first. Add a short section so they also find your Sill agent page.';
export const SHOPIFY_AGENTS_MD_STEPS: readonly string[] = [
  'In Shopify, open Online Store → Themes, then click … → Edit code on your live theme.',
  'Right-click the Templates folder, choose New file, and name it agents.md.liquid.',
  'Paste the template below and click Save. Your /llms.txt shows the same content.',
];
export const SHOPIFY_AGENTS_MD_NOTE =
  'This file replaces the one Shopify writes for you. The template keeps your store, checkout and sitemap links and adds Sill’s. To go back to Shopify’s version, delete the file.';

/** The agents.md.liquid template. Only `agents` and `request` render here. */
export function shopifyAgentsMdTemplate(domain: string): string {
  const base = `https://${domain.toLowerCase()}/apps/sill`;
  return [
    '# Agent instructions: {{ agents.store_name }}',
    '',
    "You can browse products and shop at {{ agents.store_name }} ({{ agents.store_url }}) on your user's behalf.",
    '',
    '## Shop skill',
    '',
    'Use the [Shop skill](https://shop.com/SKILL.md) to help your user shop with this store.',
    '',
    '## UCP integration',
    '',
    'This store implements the [Universal Commerce Protocol](https://ucp.dev) for product discovery, carts and checkout.',
    '',
    '1. Discover the store: `GET {{ agents.ucp_discovery_url }}` returns its UCP profile.',
    '2. Connect to the MCP endpoint `{{ agents.mcp_endpoint_url }}` and call `tools/list`.',
    "3. Place orders and submit payment only with the buyer's authorization.",
    '',
    'Supported UCP versions: {{ agents.ucp_versions | join: \', \' }}. Prices are in {{ agents.currency }}. Sitemap: {{ agents.sitemap_url }}',
    '',
    '## Agent gateway',
    '',
    `- [Agent page](${base}/agent): what this store offers agents, in plain HTML`,
    `- [Agent card](${base}/agent-card.json): signed identity and capabilities`,
    `- [AI catalog](${base}/ai-catalog.json): signed list of this store's agent endpoints`,
    '',
  ].join('\n');
}

export const SHOPIFY_AGENT_REDIRECT_TITLE = 'Point /agent to Sill';
export const SHOPIFY_AGENT_REDIRECT_SENTENCE =
  'Agents often try /agent on a store. A redirect sends them to your Sill agent page.';
export const SHOPIFY_AGENT_REDIRECT_STEPS: readonly string[] = [
  'In Shopify, open Content → Menus and click View URL redirects.',
  'Click Create URL redirect.',
  'Redirect from /agent to /apps/sill/agent, then click Save.',
];

export const SHOPIFY_CF_OBSERVE_TITLE = 'Observe every agent with Cloudflare';
export const SHOPIFY_CF_OBSERVE_LABEL = "If your domain's DNS is on Cloudflare";
export const SHOPIFY_CF_OBSERVE_SENTENCE =
  'When your store record in Cloudflare is proxied (orange cloud), the Sill Worker runs in front of Shopify and records every agent, including crawlers that never run JavaScript.';
export const SHOPIFY_CF_OBSERVE_SAFE =
  'The Worker only watches. Checkout, cart, orders and payments go straight to Shopify, and Shopify’s own /llms.txt, /agents.md and /.well-known/ucp are passed through unchanged. You do not need to connect the Shopify app for this.';
export const SHOPIFY_CF_OBSERVE_STEPS: readonly string[] = [
  'In Cloudflare → DNS → Records, make sure your store record is a CNAME to shops.myshopify.com.',
  'Turn Proxy status on (orange cloud) for that record.',
  'In SSL/TLS → Edge Certificates, leave Always Use HTTPS off. Shopify needs plain HTTP to renew your certificate.',
  'To force HTTPS, add a redirect rule instead, and exclude paths starting with /.well-known/acme-challenge/.',
  'Come back here and set up the Cloudflare Worker.',
];
/**
 * Flip to true once the Worker template passes Shopify's own files,
 * checkout and cart straight through (Shopify-safe mode). Until then the
 * Cloudflare steps show the pending note and no Worker button.
 */
export const SHOPIFY_SAFE_WORKER_AVAILABLE = false;
export const SHOPIFY_CF_OBSERVE_PENDING =
  'Available soon: the Shopify-safe version of the Worker is not released yet. Do not turn on the orange cloud for Sill until this note is gone.';
